/**
 * Typed API layer for the account details tab (VIP-12076).
 *
 * The profile fields themselves go through the customer store's updateCustomerData(),
 * which already owns PATCH /v2/customer/profile and refreshes the profile afterwards.
 * Only the credential and deletion endpoints live here.
 */

export interface PasswordChangePayload {
  current_password: string
  new_password: string
}

export interface EmailChangePayload {
  current_password: string
  email: string
}

export interface AccountDeletionPayload {
  current_password: string
  /** The API rejects anything but a literal true */
  delete_account: true
}

/**
 * The error RequestHelper throws: `cause` carries the vicampoErrorCode, `field` the
 * payload key the API blamed.
 */
export type ApiRequestError = Error & {
  status?: number
  cause?: unknown
  field?: string
  description?: string
}

export const changePassword = async (
  payload: PasswordChangePayload
): Promise<void> => {
  const { RequestHelper, API_URL } = await _getModules()
  await RequestHelper.sendPatchRequest(API_URL.CUSTOMER.PASSWORD, payload)
}

export const changeEmail = async (
  payload: EmailChangePayload
): Promise<void> => {
  const { RequestHelper, API_URL } = await _getModules()
  await RequestHelper.sendPatchRequest(API_URL.CUSTOMER.EMAIL, payload)
}

export const deleteAccount = async (
  payload: AccountDeletionPayload
): Promise<void> => {
  const { RequestHelper, API_URL } = await _getModules()
  await RequestHelper.sendDeleteRequest(API_URL.CUSTOMER.ACCOUNT, payload)
}

const _getModules = (() => {
  let cache: {
    RequestHelper: any
    API_URL: any
  } | null = null

  return async () => {
    if (cache) return cache

    const [{ default: RequestHelper }, { API_URL }] = await Promise.all([
      import('@/services/helpers/RequestHelper'),
      import('@/constants/GlobalConstants')
    ])
    cache = { RequestHelper, API_URL }

    return cache
  }
})()
